<template>
    <td
        role="cell"
        :style="containerStyle"
        :class="containerClass"
        @click="setFocus"
        :data-p-frozen-column="frozen"
        :data-p-editable-column="editableCell"
        ref="cellRef"
    >
        <div class="md-editable-grid-cell-wrapper" @dblclick="onCellDblClick">
            <!-- Editing mode -->
            <div
                v-if="isEditing"
                v-click-outside:[datePickerName]="closeDropdown"
                class="editable-date-cell-content-wrapper"
            >
                <div
                    :class="{
                        'md-editable-grid-cell-content-invalid': invalid,
                        'md-editable-grid-cell-editing': !invalid
                    }"
                    class="md-editable-grid-cell-content"
                    contenteditable="true"
                    ref="inputRef"
                    @keydown="handleKeydown"
                    @blur="finishEdit"
                >
                    {{ displayValue }}
                </div>

                <div class="editable-date-cell-icon-container" @mousedown.prevent="toggleDropdown">
                    <i>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 1024 1024"
                            width="1em"
                            height="1em"
                            fill="#19BEE8"
                        >
                            <path
                                d="M940.218182 107.054545h-209.454546V46.545455h-65.163636v60.50909H363.054545V46.545455H297.890909v60.50909H83.781818c-18.618182 0-32.581818 13.963636-32.581818 32.581819v805.236363c0 18.618182 13.963636 32.581818 32.581818 32.581818h861.090909c18.618182 0 32.581818-13.963636 32.581818-32.581818V139.636364c-4.654545-18.618182-18.618182-32.581818-37.236363-32.581819zM297.890909 172.218182V232.727273h65.163636V172.218182h307.2V232.727273h65.163637V172.218182h176.872727v204.8H116.363636V172.218182h181.527273zM116.363636 912.290909V442.181818h795.927273v470.109091H116.363636z"
                            ></path>
                        </svg>
                    </i>
                </div>
            </div>

            <!-- Display mode -->
            <div v-else class="editable-date-cell-display-wrapper">
                <span
                    class="md-editable-grid-cell-display"
                    :class="{
                        'md-editable-grid-cell-non-editable': !editable,
                        'span-placeholder': placeholderVisible
                    }"
                    @dblclick="requestEdit"
                    @keydown="handleDisplayKeydown"
                    tabindex="0"
                    ref="displayRef"
                >
                    {{ displayValue || placeholder }}
                </span>

                <!-- DateTime picker icon -->
                <div
                    v-if="editableCell"
                    class="editable-date-cell-display-icon"
                    @mousedown.prevent="requestEdit"
                >
                    <i>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 1024 1024"
                            width="1em"
                            height="1em"
                            fill="#19BEE8"
                        >
                            <path
                                d="M940.218182 107.054545h-209.454546V46.545455h-65.163636v60.50909H363.054545V46.545455H297.890909v60.50909H83.781818c-18.618182 0-32.581818 13.963636-32.581818 32.581819v805.236363c0 18.618182 13.963636 32.581818 32.581818 32.581818h861.090909c18.618182 0 32.581818-13.963636 32.581818-32.581818V139.636364c-4.654545-18.618182-18.618182-32.581818-37.236363-32.581819zM297.890909 172.218182V232.727273h65.163636V172.218182h307.2V232.727273h65.163637V172.218182h176.872727v204.8H116.363636V172.218182h181.527273zM116.363636 912.290909V442.181818h795.927273v470.109091H116.363636z"
                            ></path>
                        </svg>
                    </i>
                </div>
            </div>

            <date-picker
                v-show="isEditing && showDropdown"
                v-model="dateValue"
                :type="'date'"
                :inline="true"
                :format="dateFormat"
                :lang="lang"
                :id="datePickerName"
                input-class="form-control"
                @change="datePickerChanged"
            >
                <template v-slot:header="{ emit }">
                    <button class="mx-btn mx-btn-text mx-btn-local" @click="selectToday(emit)">
                        {{ $t('Shared.Today') }}
                    </button>
                </template>
            </date-picker>
        </div>
    </td>
</template>

<script>
import EditableCellBase from './EditableCellBase.vue';
import ShowMessage from '@/components/messages/ShowMessage.js';
import Utils from '@/common/utils.js';
import 'vue2-datepicker/index.css';

export default {
    name: 'EditableDateCell',
    inject: ['$parentInstance'],
    extends: EditableCellBase,
    mixins: [ShowMessage],
    props: {
        value: {
            type: [Date, String],
            default: null
        },
        invalidFormatAction: {
            type: String,
            default: 'initial',
            validator: value => ['null', 'initial'].includes(value)
        },
        invalidFormatWarning: {
            type: String,
            default: 'cell',
            validator: value => ['none', 'cell', 'message', 'both'].includes(value)
        }
    },
    data() {
        return {
            dropdownElement: null,
            showDropdown: false,
            dateFormat: Utils.getFormatComponentDatePicker('date'),
            displayValue: null,
            formattedValue: null,
            dateValue: null,
            dropdownRect: null
        };
    },
    computed: {
        placeholder() {
            if (this.placeholderVisible) {
                return this.dateFormat;
            }

            return '';
        },
        placeholderVisible() {
            return this.editableCell && this.dateValue === null;
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                let cleanVal = newValue;
                const parsed = cleanVal ? this.parse(cleanVal) : null;
                this.displayValue = this.formattedValue = this.formatDate(parsed);
                this.dateValue = parsed;
            }
        },
        showDropdown(val) {
            val ? this.renderDropdown() : this.removeDropdown();
        }
    },
    methods: {
        startEdit(char) {
            if (!this.editableCell) {
                this.displayContentFocus();
                return;
            }

            this.isEditing = true;
            if (char !== undefined) {
                this.$nextTick(() => {
                    const el = this.$refs.inputRef;
                    if (el) {
                        el.innerText = char;
                        el.focus();
                        this.moveCaretToEnd(el);
                    }
                });
            } else {
                this.inputContentFocus();
            }
        },
        finishEdit(event) {
            if (!this.showDropdown && this.isEditing) {
                this.isEditing = false;
                const val = event.target.innerText.trim();
                const changed = val !== String(this.formattedValue).trim();

                if (changed) {
                    const parsed = this.parse(val);
                    const valISO = this.emitInternalDate(parsed);
                    const valid = this.validateCell(valISO);

                    if (!valid) {
                        this.showMessage(
                            this.$tc('Msg.Error'),
                            this.invalidMessage(this.name),
                            'errorAutoHide'
                        );
                    }
                }
            }
        },
        closeDropdown() {
            this.showDropdown = false;
            this.finishEdit({ target: this.$refs.inputRef });
        },
        removeDropdown() {
            /*if (this.dropdownElement?.parentNode) {
                this.dropdownElement.parentNode.removeChild(this.dropdownElement);
                this.dropdownElement = null;
            }*/
        },
        toggleDropdown() {
            if (!this.editableCell) {
                return;
            }

            this.showDropdown = !this.showDropdown;

            if (this.showDropdown) {
                const val = this.$refs.inputRef.innerText.trim();
                this.dateValue = this.parse(val);
                this.$refs.inputRef.focus();
            }
        },
        renderDropdown() {
            this.$nextTick(() => {
                if (!this.$refs.cellRef) {
                    return;
                }

                const datePicker = document.getElementById(this.datePickerName);
                if (!datePicker) {
                    return;
                }

                var _this$_popupRect = this.dropdownRect,
                    width = _this$_popupRect.width,
                    height = _this$_popupRect.height;

                const _getRelativePosition = this.getRelativePosition(
                        this.$refs.cellRef,
                        width,
                        height,
                        true
                    ),
                    left = _getRelativePosition.left,
                    top = _getRelativePosition.top;

                Object.assign(datePicker.style, {
                    position: 'absolute',
                    top: top,
                    left: left,
                    zIndex: '999999'
                    //display: 'block'
                });
            });
        },
        requestEdit() {
            EditableCellBase.methods.requestEdit.call(this);

            this.$nextTick(() => {
                if (this.isEditing && !this.showDropdown) {
                    this.toggleDropdown();
                }
            });
        },

        parse(val) {
            if (!val) return null;

            if (val instanceof Date) return val;

            if (typeof val === 'string') {
                if (val.includes('T')) {
                    const [datePart, timePart] = val.split('T');
                    const [year, month, day] = datePart.split('-').map(Number);

                    if (this.Type === 'date') {
                        return new Date(year, month - 1, day);
                    }

                    let hour = 0;
                    let minute = 0;

                    if (timePart) {
                        const [hh, mm] = timePart.split(':');
                        hour = parseInt(hh, 10) || 0;
                        minute = parseInt(mm, 10) || 0;
                    }

                    return new Date(year, month - 1, day, hour, minute);
                }

                // yyyy-MM-dd
                if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
                    const [year, month, day] = val.split('-').map(Number);
                    return new Date(year, month - 1, day);
                }
            }

            // manual
            const parts = val.split('/');
            if (parts.length !== 3 || parts.some(p => !p)) {
                return this.dateValue;
            }

            let day, month, year;
            if (this.dateFormat && this.dateFormat.startsWith('DD')) {
                day = +parts[0];
                month = +parts[1];
                year = +parts[2];
            } else {
                month = +parts[0];
                day = +parts[1];
                year = +parts[2];
            }

            return this.isValidDate(year, month, day)
                ? new Date(year, month - 1, day)
                : this.dateValue;
        },

        emitInternalDate(val) {
            if (!val) {
                if (this.isAppendSection) {
                    this.$emit('input', null);
                } else {
                    this.$parentInstance.cellEditComplete(this.createEventData(null));
                }

                return null;
            }

            val = new Date(val.getFullYear(), val.getMonth(), val.getDate());

            const pad = n => String(n).padStart(2, '0');
            const yyyy = val.getFullYear();
            const mm = pad(val.getMonth() + 1);
            const dd = pad(val.getDate());

            let result = `${yyyy}-${mm}-${dd}`;

            if (this.isAppendSection) {
                this.$emit('input', result);
            } else {
                this.$parentInstance.cellEditComplete(this.createEventData(result));
            }

            return result;
        },

        isValidDate(year, month, day) {
            if (month < 1 || month > 12) {
                return false;
            }

            const maxDays = [
                31,
                (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28,
                31,
                30,
                31,
                30,
                31,
                31,
                30,
                31,
                30,
                31
            ];

            return day >= 1 && day <= maxDays[month - 1];
        },

        formatDate(date) {
            if (!date) {
                return null;
            }

            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();

            return this.dateFormat.startsWith('DD')
                ? `${day}/${month}/${year}`
                : `${month}/${day}/${year}`;
        },

        handleKeydown(event) {
            if (/^[0-9]$/.test(event.key) || event.key === '/') {
                return;
            }

            const allowedKeys = [
                'Backspace',
                'Tab',
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Delete',
                'Escape',
                'Enter'
            ];

            if (allowedKeys.includes(event.key)) {
                switch (event.key) {
                    case 'Enter':
                    case 'Tab':
                    case 'Escape':
                        this.showDropdown = false;
                        break;
                    case 'ArrowUp':
                        if (this.caretAtStart()) {
                            this.showDropdown = false;
                        }
                        break;
                    case 'ArrowDown':
                        if (this.caretAtEnd()) {
                            this.showDropdown = false;
                        }
                        break;
                    case 'ArrowLeft':
                        if (this.caretAtStart() && !this.isAllSelected()) {
                            this.showDropdown = false;
                        }
                        break;
                    case 'ArrowRight':
                        if (this.caretAtEnd() && !this.isAllSelected()) {
                            this.showDropdown = false;
                        }
                        break;
                }

                EditableCellBase.methods.handleKeydown.call(this, event);
                return;
            }

            event.preventDefault();
        },
        datePickerChanged() {
            const formattedValue = this.formatDate(this.dateValue);
            this.showDropdown = false;

            const el = this.$refs.inputRef;
            if (el) {
                el.innerText = formattedValue;
                el.focus();
                this.moveCaretToEnd(el);
            }
        },
        selectToday() {
            this.dateValue = new Date();
            this.datePickerChanged();
        }
        /*setFocus() {
      this.$emit('cellFocus', this.row, this.name);
    },
    requestEdit() {
      if (!this.editable) return;
      this.$emit('requestEdit', { row: this.row, name: this.name });
    },
    startEdit() {
      if (!this.editable) {
        this.$nextTick(() => this.$refs.displayRef.focus());
        return;
      }

      this.isEditing = true;
      this.$nextTick(() => {
        const el = this.$refs.inputRef;
        el.focus();

        // Seleccionar todo el contenido
        const range = document.createRange();
        range.selectNodeContents(el);

        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      });
    },
    finishEdit() {
      const format = Utils.getShortDateFormat();
      const valid = this.validateCell(this.displayValue);

      if (valid) {
        const iso = Utils.formatterToISO(this.displayValue, format);
        this.isEditing = false;
        const changed = iso !== Utils.formatterToISO(this.value);

        if (changed) {
          this.$emit('input', iso);
          this.emitChanged(iso);
        }
      } else {
        this.isEditing = true;
        //this.$emit('cellFocus', this.row, this.name);
        //this.$nextTick(() => this.$refs.inputRef.focus());
        this.showMessage(this.$tc('Msg.Error'), this.invalidMessage(this.name), "errorAutoHide");
      }
    },

    handleKeydown(event) {
      // Detectar Ctrl+V o Cmd+V manualmente
      if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        // Si el navegador no lanza el evento paste por sí solo,
        // lo dejamos pasar para que lo intente o lo manejamos aquí.
        return;
      }

      this.isDeleting = event.key === 'Backspace' || event.key === 'Delete';
      const caret = this.getCaretPosition(this.$refs.inputRef);

      // Si el usuario escribe "/" en la posición 2 o 5, solo movemos el cursor
      if (event.key === '/') {
        if (caret === 2 || caret === 5) {
          // No hacemos nada, el cursor ya está en la posición de la barra
          return;
        }
        event.preventDefault();
        return;
      }

      const allowedKeys = ['Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Tab','Enter'];
      if (allowedKeys.includes(event.key)) {
        switch (event.key) {
          case 'Enter':
            event.preventDefault();
            this.finishEdit(event);

            if (!this.invalid) this.$emit('nextCell');
            break;
          case 'Tab':
            event.preventDefault();
            this.finishEdit(event);

            if (!this.invalid) {
              this.$emit(event.shiftKey ? 'prevCell' : 'nextCell');
            }
            break;
          case 'ArrowUp':
            if (this.caretAtStart()) {
              event.preventDefault();
              this.finishEdit(event);

              if (!this.invalid) this.$emit('upCell');
            }
            break;
          case 'ArrowDown':
            if (this.caretAtEnd()) {
              event.preventDefault();
              this.finishEdit(event);

              if (!this.invalid) this.$emit('downCell');
            }
            break;
          case 'ArrowLeft':
            if (this.caretAtStart()) {
              event.preventDefault();
              this.finishEdit(event);

              if (!this.invalid) this.$emit('prevCell');
            }
            break;
          case 'ArrowRight':
            if (this.caretAtEnd()) {
              event.preventDefault();
              this.finishEdit(event);

              if (!this.invalid) this.$emit('nextCell');
            } break;
        }

        return;
      }

      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
      }
    },

    handleDisplayKeydown(event) {
      switch (event.key) {
        case 'Enter':
          event.preventDefault();

          if (this.editable) {
            this.requestEdit();
          } else {
            this.$emit('nextCell');
          }
          break;

        case 'F2': // opcional, estilo Excel
          if (this.editable) {
            event.preventDefault();
            this.requestEdit();
          }
          break;

        case 'Tab':
          event.preventDefault();
          this.$emit(event.shiftKey ? 'prevCell' : 'nextCell');
          break;

        case 'ArrowUp':
          event.preventDefault();
          this.$emit('upCell');
          break;

        case 'ArrowDown':
          event.preventDefault();
          this.$emit('downCell');
          break;

        case 'ArrowLeft':
          event.preventDefault();
          this.$emit('prevCell');
          break;

        case 'ArrowRight':
          event.preventDefault();
          this.$emit('nextCell');
          break;
      }
    },

    handleInput(e) {
      console.log('handleInput')
      const el = e.target;
      const originalText = el.textContent;

      // 1. Obtenemos posición inicial del cursor
      const caretBefore = this.getCaretPosition(el);

      // 2. Extraemos solo los números
      let digits = originalText.replace(/\D/g, '').slice(0, 8);
      this.rawDigits = digits;

      // 3. Aplicamos la máscara (la lógica que ya tienes con keepTrailingSlash)
      const endsWithSlash = originalText.endsWith('/');
      const masked = this.applyMask(digits, endsWithSlash);

      // 4. Si el texto formateado es distinto al actual, actualizamos el DOM
      if (masked !== originalText) {
        el.textContent = masked;

        this.$nextTick(() => {
          // CALCULO DEL NUEVO CURSOR:
          // Si el usuario añadió un número y se insertó una barra automática,
          // la longitud de la cadena creció más de lo que el usuario escribió.
          let newCaret = caretBefore;

          // Si no estamos borrando y se insertó una barra justo antes o en la posición del cursor
          if (!this.isDeleting) {
            // Caso crítico: Escribes el 3er o 5to dígito y aparece la barra
            // Si el carácter en la posición del cursor (o justo antes) es ahora una barra, saltamos.
            if (masked.length > originalText.length) {
              // Si el cursor estaba al final o sobre la posición de la nueva barra
              if (caretBefore === 3 || caretBefore === 6) {
                newCaret = caretBefore + 1;
              }
            }
          }

          this.setCaretPosition(el, Math.min(newCaret, masked.length));
        });
      }
      this.displayValue = masked;
    },

    handlePaste(e) {
      e.preventDefault(); // Detenemos el pegado nativo para tener control total

      // 1. Obtenemos el texto plano del portapapeles
      const text = (e.clipboardData || window.clipboardData).getData('text/plain');

      // 2. Extraemos solo los dígitos (máximo 8)
      const digits = text.replace(/\D/g, '').slice(0, 8);
      if (!digits) return;

      // 3. Insertamos los dígitos en la posición actual del cursor
      const selection = window.getSelection();
      if (!selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      range.deleteContents(); // Borra lo seleccionado si hay algo

      const textNode = document.createTextNode(digits);
      range.insertNode(textNode);

      // 4. Movemos el cursor al final de lo insertado
      range.setStartAfter(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);

      // 5. IMPORTANTE: Disparamos manualmente el evento 'input'
      // Esto hace que se ejecute tu método onInput(e), el cual aplicará
      // la máscara (MM/DD/YYYY) y validará los datos.
      const event = new Event('input', { bubbles: true });
      this.$refs.inputRef.dispatchEvent(event);
    },

    caretAtStart() {
      const sel = window.getSelection();
      return sel.anchorOffset === 0;
    },

    caretAtEnd() {
      const sel = window.getSelection();
      const el = this.$refs.inputRef;
      return sel.anchorOffset === el.innerText.length;
    },

    applyMask(digits, keepTrailingSlash) {
      if (!digits) return "";
      let r = "";

      // Bloque 1 (Día o Mes)
      r += digits.substring(0, 2);
      if (digits.length > 2) {
        r += "/" + digits.substring(2, 4);
      } else if (digits.length === 2 && keepTrailingSlash) {
        r += "/"; // Mantiene la barra si el usuario la escribió o borró hasta ahí
      }

      // Bloque 2 (Mes o Día)
      if (digits.length > 4) {
        r += "/" + digits.substring(4, 8);
      } else if (digits.length === 4 && keepTrailingSlash) {
        r += "/";
      }

      return r;
    },

    getCaretPosition(el) {
      const sel = window.getSelection();
      if (sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(el);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        return preCaretRange.toString().length;
      }
      return 0;
    },

    setCaretPosition(el, pos) {
      const sel = window.getSelection();
      const range = document.createRange();

      // Nos aseguramos de que el elemento tenga un nodo de texto limpio
      if (!el.firstChild) {
        el.appendChild(document.createTextNode(""));
      }

      let charCount = 0;
      let nodeStack = [el];
      let node;

      while ((node = nodeStack.pop())) {
        if (node.nodeType === 3) {
          const nextCount = charCount + node.length;
          if (pos <= nextCount) {
            range.setStart(node, pos - charCount);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
            return;
          }
          charCount = nextCount;
        } else {
          let i = node.childNodes.length;
          while (i--) nodeStack.push(node.childNodes[i]);
        }
      }

      // Fallback al final del contenido si falla el loop
      const last = el.lastChild;
      range.setStart(last, last.nodeType === 3 ? last.length : 0);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    },

    emitChanged(value) {
      EventBus.$emit('onChanged', { Changed: true, componentName: this.name, value });
    },
    validateCell(val = null) {
      let state = true;
      const valueToCheck = val !== null ? val : String(this.displayValue ?? '').trim();
      const validatorRules = this.validatorRules ? [...this.validatorRules] : [];

      if (valueToCheck.length !== 0) {
        validatorRules.push(
          validationRules.length(10,
            this.$t('Msg.InvalidDate')
          ),
          validationRules.date(Utils.getShortDateFormat(),
              this.$t('Msg.InvalidDate')
          )
        );
      }

      if (validatorRules.length > 0) {
        const rules = {
          [this.name]: validatorRules
        };
        state = this.validateFields(rules, { [this.name]: valueToCheck });
      }

      this.invalid = !state;
      if (this.isFocused) {
        this.$emit('validationState', { name: this.name, invalid: this.invalid });
      }

      return state;
    },*/
    },

    mounted() {
        const datePicker = document.getElementById(this.datePickerName);

        if (datePicker) {
            this.dropdownRect = this.getElementSize(datePicker);
            document.body.appendChild(datePicker);
        }
        /*const el = this.$refs.inputRef;
    if (el) {
      el.addEventListener('paste', this.handlePaste);
    }*/
    },

    beforeDestroy() {
        // o beforeUnmount() en Vue 3
        /*const el = this.$refs.inputRef;
    if (el) {
      el.removeEventListener('paste', this.handlePaste);
    }*/
    },
    directives: {
        'click-outside': {
            bind(el, binding, vnode) {
                el.clickOutsideEvent = e => {
                    const datePicker = document.getElementById(binding.arg);
                    const insideDatePicker = datePicker && datePicker.contains(e.target);

                    if (!(el === e.target || el.contains(e.target) || insideDatePicker)) {
                        vnode.context[binding.expression]();
                    }
                };
                document.body.addEventListener('mousedown', el.clickOutsideEvent);
            },
            unbind(el) {
                document.body.removeEventListener('mousedown', el.clickOutsideEvent);
            }
        }
    }
};
</script>

<style scoped>
.md-editable-grid-cell-display {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.editable-date-cell-content-wrapper {
    display: flex;
    align-items: stretch;
    width: 100%;
    height: 100%;
    position: relative;
}

.editable-date-cell-icon-container {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #19bee8;
    cursor: pointer;
    z-index: 11;
}
tbody.md-editable-grid-body-append .editable-date-cell-icon-container {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #19bee8;
    cursor: pointer;
    z-index: 11;
}

/* Display mode: flex-based so the icon stays within the cell scroll area */
.editable-date-cell-display-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    overflow: hidden;
}
.editable-date-cell-display-wrapper .md-editable-grid-cell-display {
    flex: 1;
    min-width: 0;
}
.editable-date-cell-display-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding-right: 6px;
    color: #19bee8;
    cursor: pointer;
}
tbody.md-editable-grid-body-append .editable-date-cell-display-icon {
    color: #19bee8;
}
.span-placeholder {
    color: lightgrey;
}
</style>
